'use client';

import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectItem, SelectTrigger } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Eye, EyeOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { useUpdateUserMutation } from '@flysync/shared-redux/services/booking';
import { useRouter } from '@/lib/navigation';
// import { config } from '@/config';

function Profile() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { toast } = useToast();

  const router = useRouter();

  const [userData, setUserData] = useState({});

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storedUser = sessionStorage.getItem('user');
      if (storedUser) {
        setUserData(JSON.parse(storedUser));
      }
    }
  }, []);

  const [updateUser] = useUpdateUserMutation();
  const [showChangePassword, setShowChangePassword] = useState(false);

  // const bgImage = `/${config.companyName}/bg.png`;

  const [user, setUser] = useState({
    // firstName: '',
    lastName: '',
    mobileNumber: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  useEffect(() => {
    const getUserDetailsResponse = async () => {
      const {
        user_name = '',
        email = '',
        user_mobile = '',
      } = userData || JSON.parse(sessionStorage.getItem('user') || '{}');
      setUser({
        lastName: user_name || '',
        mobileNumber: user_mobile || '',
        email: email || '',
        password: '',
        confirmPassword: '',
      });
    };
    getUserDetailsResponse();
  }, [userData]);

  const userSchema = z
    .object({
      // firstName: z.string().min(1, 'must contain at least 1 character'),
      lastName: z.string().min(1, 'must contain at least 1 character'),
      mobileNumber: z.string().min(10, 'must contain at least 10 character'),
      email: z.string().email(),
      password: showChangePassword
        ? z.string().min(6, 'Password must be at least 6 characters')
        : z.string().optional(),
      confirmPassword: z.string(),
    })
    .refine(
      (data) => {
        // Only validate passwords if either field is filled
        if (data.password || data.confirmPassword) {
          return data.password === data.confirmPassword;
        }
        return true; // Skip validation if both fields are empty
      },
      {
        message: 'Passwords do not match',
        path: ['confirmPassword'],
      },
    );

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: string,
  ) => {
    const getUser: any = { ...user };
    getUser[field] = e.target.value;
    setUser(getUser);
  };

  const handleSaveChanges = async () => {
    const validationResult = userSchema.safeParse(user);

    if (!validationResult.success) {
      const errorData = validationResult.error;
      let errorMessages: string[] = [];

      if (errorData instanceof z.ZodError) {
        const newErrors = errorData.errors.reduce(
          (acc: any, err: any) => {
            const field = err.path[err.path.length - 1];
            if (!acc[field]) {
              acc[field] = err.message;
            }
            const fieldName =
              typeof field === 'string'
                ? field.charAt(0).toUpperCase() +
                  field.slice(1).toLowerCase().replace(/_/g, ' ')
                : 'Field';
            errorMessages.push(`${fieldName} ${err.message}.`);

            return acc;
          },
          {} as Record<string, string>,
        );
      }

      toast({
        title: 'Validation failed',
        description: errorMessages.join('\n'),
        variant: 'error',
      });
      return;
    }

    // If passwords are provided but don't match
    if (
      (user.password || user.confirmPassword) &&
      user.password !== user.confirmPassword
    ) {
      toast({
        title: 'Password mismatch',
        description: 'Password and Confirm Password must match',
        variant: 'error',
      });
      return;
    }

    try {
      const reqData = {
        userId: JSON.parse(sessionStorage.getItem('user') || '{}')?.user_id,
        mobile: user.mobileNumber,
        name: user.lastName,
        // Only include password if it's provided
        ...(user.password && { password: user.password }),
      };

      const updateUserResponse = (await updateUser(reqData).unwrap()) as {
        status: { code: number; message: string };
      };
      if (updateUserResponse.status.code === 200) {
        sessionStorage.setItem(
          'user',
          JSON.stringify(updateUserResponse.status.data),
        );
        toast({
          title: 'User updated successfully',
          description: 'User data has been updated successfully',
          variant: 'success',
        });
        router.push('/');
      } else {
        toast({
          title: 'User update failed',
          description: updateUserResponse.status.message,
          variant: 'error',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update user',
        variant: 'error',
      });
    }
  };

  return (
    <div className="relative bg-tertiary">
      <div
        className="h-14 w-full bg-primary bg-cover bg-center sm:h-20 lg:h-56"
        // style={{ backgroundImage: `url(${bgImage})` }}
      />
      <div className="absolute inset-x-0 top-0 mx-auto px-1 pb-4 sm:top-12 sm:w-[1200px] sm:pb-8 lg:top-12 lg:pb-20">
        <div className="mb-3 flex items-center gap-2 sm:mb-5 lg:mb-7">
          <h2 className="pl-2 text-base font-semibold text-white sm:pl-4 sm:text-lg lg:pl-8 lg:text-xl">
            My Profile
          </h2>
        </div>
        <div className="rounded-xl bg-white p-4 shadow-lg sm:rounded-2xl sm:p-8 lg:rounded-3xl lg:p-12">
          <div className="flex items-center gap-2 rounded-lg bg-background p-3 sm:gap-3 sm:p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-300 sm:h-11 sm:w-11 lg:h-12 lg:w-12">
              <Avatar className="h-full w-full">
                <AvatarImage
                  src="https://github.com/shadcn.png"
                  className="h-full w-full object-cover"
                />
                {/* <AvatarFallback className="text-base sm:text-lg lg:text-xl">
                  {user.firstName.charAt(0)}
                </AvatarFallback> */}
              </Avatar>
            </div>
            <div className="flex flex-col items-start gap-0.5 sm:flex-row sm:items-center sm:gap-2">
              <h2 className="line-clamp-1 text-base font-semibold text-gray-800 sm:text-lg">
              {user.lastName}
              </h2>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 sm:mt-8 sm:grid-cols-2 sm:gap-6 lg:mt-12 lg:gap-x-24 lg:gap-y-7">
            {/* <Input
              type="text"
              placeholder="User Name"
              name="firstName"
              value={user.firstName}
              onInput={(e) => handleChange(e, 'firstName')}
              disabled={false}
              tabIndex={1}
              className={`border-light h-9 rounded-md border bg-background px-3 py-2 text-xs focus:outline-none sm:h-10 sm:text-sm`}
            /> */}
            <Input
              type="text"
              placeholder="User Name"
              name="lastName"
              value={user.lastName}
              onInput={(e) => handleChange(e, 'lastName')}
              disabled={false}
              tabIndex={2}
              className={`border-light h-9 rounded-md border bg-background px-3 py-2 text-xs focus:outline-none sm:h-10 sm:text-sm`}
            />

            <Input
              placeholder="Mobile Number"
              type="text"
              name="mobileNumber"
              value={user.mobileNumber}
              onInput={(e) => handleChange(e, 'mobileNumber')}
              maxLength={10}
              disabled={false}
              tabIndex={3}
              className={`border-light h-9 rounded-md border bg-background px-3 py-2 text-xs focus:outline-none sm:h-10 sm:text-sm`}
            />

            <Input
              type="email"
              placeholder="Email ID"
              name="email"
              value={user.email}
              onInput={(e) => handleChange(e, 'email')}
              disabled={true}
              tabIndex={4}
              className={`border-light h-9 rounded-md border bg-background px-3 py-2 text-xs opacity-70 focus:outline-none sm:h-10 sm:text-sm`}
            />

            <div className="hidden sm:block"></div>
            <div
              className="hover:text-primary/90 cursor-pointer text-right text-xs text-primary transition-colors sm:text-sm col-span-2"
              onClick={() => setShowChangePassword(!showChangePassword)}
            >
              {userData?.password
                ? showChangePassword
                  ? 'Hide Password'
                  : 'Change Password'
                : 'Set Password'}
            </div>

            {showChangePassword && (
              <>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create Password"
                    name="password"
                    value={user.password}
                    onInput={(e) => handleChange(e, 'password')}
                    tabIndex={5}
                    className={`border-light h-9 rounded-md border bg-background px-3 py-2 text-xs focus:outline-none sm:h-10 sm:text-sm`}
                  />
                  <span
                    className="absolute right-3 top-2.5 cursor-pointer text-gray-500 transition-colors hover:text-gray-700 sm:top-3"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                    ) : (
                      <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
                    )}
                  </span>
                </div>

                <div className="relative">
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm Password"
                    name="confirmPassword"
                    value={user.confirmPassword}
                    onInput={(e) => handleChange(e, 'confirmPassword')}
                    tabIndex={6}
                    className={`border-light h-9 rounded-md border bg-background px-3 py-2 text-xs focus:outline-none sm:h-10 sm:text-sm`}
                  />
                  <span
                    className="absolute right-3 top-2.5 cursor-pointer text-gray-500 transition-colors hover:text-gray-700 sm:top-3"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                    ) : (
                      <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
                    )}
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Submit Button */}
          <div className="mt-8 text-center sm:mt-12 lg:mt-16">
            <Button
              className="hover:bg-primary/90 focus:ring-primary/20 h-10 w-full rounded-md bg-primary text-sm font-semibold text-white transition-all hover:shadow-xl focus:ring-2 sm:h-11 sm:w-72 sm:text-base md:w-1/4 lg:h-12 lg:text-lg"
              onClick={handleSaveChanges}
              tabIndex={7}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;
